// Components
import { Select, MultiSelect, Checkbox, TextInput } from "@mantine/core";
import { FormField } from "@benchmark/ui/components/atomic";
import { FormGroup } from "@benchmark/ui/components/atomic";
import { FormSection } from "@benchmark/ui/components/atomic";

// Constants
import {
  getRaceOptions,
  getEthnicityOptions,
  getGenderIdentityOptions,
  getSexualOrientationOptions,
} from "@benchmark/core/constants";

// Types
import type { UseFormReturnType } from "@mantine/form";
import { PatientFormData } from "@benchmark/core/types";

/**
 * Props for DemographicsSection component.
 * @agentic @benchmark/core
 */
type DemographicsSectionProps = {
  form: UseFormReturnType<PatientFormData>;
  title?: string;
  subtitle?: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
};

/**
 * Form section for patient demographics (race, ethnicity, gender identity, pronouns, sexual orientation, veteran status).
 * @agentic @benchmark/core
 */
export const DemographicsSection = ({
  form,
  title = "Demographics",
  subtitle,
  collapsible = false,
  defaultCollapsed = false,
}: DemographicsSectionProps) => {
  return (
    <FormSection
      title={title}
      subtitle={subtitle}
      collapsible={collapsible}
      defaultCollapsed={defaultCollapsed}
    >
      <FormGroup>
        <FormField label="Race">
          <Select
            placeholder="Select race"
            data={getRaceOptions()}
            searchable
            clearable
            {...form.getInputProps("race")}
          />
        </FormField>

        <FormField label="Ethnicity">
          <Select
            placeholder="Select ethnicity"
            data={getEthnicityOptions()}
            searchable
            clearable
            {...form.getInputProps("ethnicity")}
          />
        </FormField>
      </FormGroup>

      <FormGroup>
        <FormField label="Gender Identity">
          <Select
            placeholder="Select gender identity"
            data={getGenderIdentityOptions()}
            searchable
            clearable
            {...form.getInputProps("gender-identity")}
          />
        </FormField>

        <FormField label="Pronouns">
          <TextInput
            placeholder="e.g. she/her, they/them"
            maxLength={200}
            {...form.getInputProps("pronouns")}
          />
        </FormField>

        <FormField label="Sexual Orientation">
          <MultiSelect
            placeholder="Select sexual orientation(s)"
            data={getSexualOrientationOptions()}
            searchable
            clearable
            {...form.getInputProps("sexual-orientation")}
          />
        </FormField>
      </FormGroup>

      <FormField label="Veteran Status">
        <Checkbox
          label="Military Veteran"
          checked={form.values["veteran-status"] || false}
          onChange={(e) =>
            form.setFieldValue("veteran-status", e.currentTarget.checked)
          }
        />
      </FormField>
    </FormSection>
  );
};
